import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, X, Mail, UserPlus, Loader2, Check, AlertCircle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { DBCaseUserThunks, caseUserSelectByQuery } from '../../redux/thunks/DBServiceThunks';
import { CaseUserRole } from '../../models/case-user';
import { Alert, AlertDescription } from '../ui/alert';
import feathersClient from '../../feathers';
import organizationService from '../../services/organization.service';
import { setMembers } from '../../redux/slices/organizationSlice';

interface ShareCaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    caseId: string;
}

const ShareCaseModal: React.FC<ShareCaseModalProps> = ({
    isOpen,
    onClose,
    caseId
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [email, setEmail] = useState('');
    const [selectedRole, setSelectedRole] = useState<CaseUserRole>('viewer');
    const [isInviting, setIsInviting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const currentUser = useAppSelector(state => state.DBUserReducer.entities[Object.keys(state.DBUserReducer.entities)[0]]);
    const currentCase = useAppSelector(state => state.DBCaseReducer.entities[caseId]);
    const caseUsers = useAppSelector(state => caseUserSelectByQuery(state.DBCaseUserReducer, { caseId }));
    const organizationMembers = useAppSelector(state => state.organization.members);

    // Helper to get organization role for a user
    // matchByStytchId: true = match by stytchMemberId (for case users)
    //                  false = match by _id (for current user)
    const getOrganizationRole = (id: string, matchByStytchId: boolean = false): 'owner' | 'admin' | 'member' | null => {
        const member = matchByStytchId
            ? organizationMembers.find(m => m.stytchMemberId === id)
            : organizationMembers.find(m => m._id === id);
        return member?.organizationRole || null;
    };

    // Check if current user is organization admin/owner (match by database _id)
    const currentUserOrgRole = getOrganizationRole(currentUser?._id || '', false);
    const isCurrentUserAdmin = currentUserOrgRole === 'admin' || currentUserOrgRole === 'owner';

    // Load case-users and organization members when modal opens
    useEffect(() => {
        const loadData = async () => {
            if (isOpen && caseId) {
                // Load case users
                dispatch(DBCaseUserThunks.find({ caseId }));

                // Load organization members if not already loaded
                if (organizationMembers.length === 0) {
                    try {
                        const members = await organizationService.getMembers();
                        dispatch(setMembers(members));
                    } catch (err) {
                        console.error('Failed to load organization members:', err);
                    }
                }
            }
        };

        loadData();
    }, [isOpen, caseId, dispatch, organizationMembers.length]);

    const handleInvite = async () => {
        if (!email.trim()) {
            setError(t('errors.validation.emailRequired', 'Email is required'));
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError(t('errors.validation.invalidEmail', 'Please enter a valid email'));
            return;
        }

        setIsInviting(true);
        setError(null);
        setSuccess(null);

        try {
            // Get authentication token
            const auth = await feathersClient.get('authentication');
            const token = auth?.accessToken;

            // Call share-by-email route via direct HTTP POST
            const apiUrl = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:3030';
            const res = await fetch(`${apiUrl}/case-users/share-by-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    caseId,
                    email: email.toLowerCase(),
                    role: selectedRole
                })
            });

            const response = await res.json();

            if (!res.ok) {
                throw new Error(response.message || t('errors.caseSharing.failedToShare', 'Failed to share case'));
            }

            if (response.success) {
                setSuccess(response.message);
                setEmail('');
                setSelectedRole('viewer');

                // Refresh case-users list
                dispatch(DBCaseUserThunks.find({ caseId }));
            } else {
                // Handle pending invitation case
                setError(response.message);
            }
        } catch (err: any) {
            console.error('Failed to share case:', err);
            const errorMessage = err.message || t('components.caseSharing.inviteFailed', 'Failed to share case');
            setError(errorMessage);
        } finally {
            setIsInviting(false);
        }
    };

    const handleRemoveAccess = async (caseUserId: string) => {
        try {
            await dispatch(DBCaseUserThunks.delete({
                entity: { _id: caseUserId },
                options: { inCaseOfErrorRollbackOptimisticUpdateDisabled: false }
            })).unwrap();
            setSuccess(t('components.caseSharing.accessRemoved', 'Access removed successfully'));

            // Refresh list
            dispatch(DBCaseUserThunks.find({ caseId }));
        } catch (err: any) {
            console.error('Failed to remove access:', err);
            setError(err.message || t('components.caseSharing.removeFailed', 'Failed to remove access'));
        }
    };

    const handleChangeRole = async (caseUserId: string, newRole: CaseUserRole) => {
        try {
            await dispatch(DBCaseUserThunks.patch({
                entity: { _id: caseUserId, role: newRole },
                options: {} as any
            } as any)).unwrap();

            // Refresh list and wait for it to complete
            await dispatch(DBCaseUserThunks.find({ caseId })).unwrap();

            setSuccess(t('components.caseSharing.roleChanged', 'Role updated successfully'));
        } catch (err: any) {
            console.error('Failed to change role:', err);
            setError(err.message || t('components.caseSharing.roleChangeFailed', 'Failed to update role'));
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Share2 className="w-5 h-5" />
                        {t('components.caseSharing.shareCase', 'Share Case')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('components.caseSharing.shareCaseDescription', `Share "${currentCase?.title || ''}" with your team members`)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Success/Error Messages */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {success && (
                        <Alert className="border-green-500 bg-green-50 text-green-900">
                            <Check className="h-4 w-4 text-green-600" />
                            <AlertDescription>{success}</AlertDescription>
                        </Alert>
                    )}

                    {/* Invite Section */}
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="text-sm font-medium">
                                {t('components.caseSharing.inviteByEmail', 'Invite by email')}
                            </Label>
                            <div className="flex gap-2 mt-2">
                                <div className="flex-1 space-y-2">
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder={t('components.caseSharing.emailPlaceholder', 'colleague@example.com')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                                        disabled={isInviting}
                                    />
                                </div>
                                <Select
                                    value={selectedRole}
                                    onValueChange={(value) => setSelectedRole(value as CaseUserRole)}
                                    disabled={isInviting}
                                >
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="viewer">
                                            {t('components.caseSharing.viewer', 'Viewer')}
                                        </SelectItem>
                                        <SelectItem value="editor">
                                            {t('components.caseSharing.editor', 'Editor')}
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button onClick={handleInvite} disabled={isInviting} size="default">
                                    {isInviting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <UserPlus className="w-4 h-4" />
                                    )}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {t('components.caseSharing.viewerDescription', 'Viewer: Can view case and chats')} •{' '}
                                {t('components.caseSharing.editorDescription', 'Editor: Can edit and create chats')}
                            </p>
                        </div>
                    </div>

                    {/* Shared Users List */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">
                            {t('components.caseSharing.sharedWith', 'Shared with')} ({caseUsers.length})
                        </Label>

                        {caseUsers.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">
                                    {t('components.caseSharing.noSharedUsers', 'This case hasn\'t been shared yet')}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                {caseUsers.map((caseUser) => {
                                    // Check organization member status for real-time pending state
                                    // This is more accurate than case-user memberStatus which may be stale
                                    const orgMember = organizationMembers.find(
                                        m => m.stytchMemberId === caseUser.userId
                                    );
                                    // Stytch uses 'invited' status for pending members
                                    const isPending = orgMember?.status === 'pending' ||
                                        (orgMember?.status as string) === 'invited' ||
                                        caseUser.memberStatus === 'pending';
                                    const isOwner = getOrganizationRole(caseUser.userId, true) === 'owner';

                                    return (
                                        <div
                                            key={caseUser._id}
                                            className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 flex-1">
                                                <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center">
                                                    <span className="text-sm font-medium text-gold">
                                                        {(caseUser.userEmail || caseUser.userId).charAt(0).toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-medium">{caseUser.userEmail || caseUser.userId}</p>
                                                        {isPending && (
                                                            <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">
                                                                {t('components.caseSharing.pending')}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    {caseUser.createdAt && !isNaN(new Date(caseUser.createdAt).getTime()) && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {isPending
                                                                ? t('components.caseSharing.invitedOn', 'Invited on')
                                                                : t('components.caseSharing.sharedOn', 'Shared on')
                                                            } {new Date(caseUser.createdAt).toLocaleDateString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <Select
                                                    value={caseUser.role}
                                                    onValueChange={(value) => handleChangeRole(caseUser._id, value as CaseUserRole)}
                                                    disabled={!isCurrentUserAdmin || isOwner || isPending}
                                                >
                                                    <SelectTrigger className="w-[120px] h-8 text-xs">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="viewer">
                                                            {t('components.caseSharing.viewer', 'Viewer')}
                                                        </SelectItem>
                                                        <SelectItem value="editor">
                                                            {t('components.caseSharing.editor', 'Editor')}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveAccess(caseUser._id)}
                                                    className="h-8 w-8 p-0"
                                                    disabled={!isCurrentUserAdmin || isOwner}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={onClose}>
                        {t('common.buttons.close', 'Close')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ShareCaseModal;
